import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd'; // theme eklendi
import './App.scss';
import AuthLayout from './layouts/AuthLayout';
import Login from './views/Auth/Login';
import Register from './views/Auth/Register';
import EmailVerification from './views/EmailVerification';
import ForgotPassword from './views/Auth/ForgotPassword';
import MainLayout from './layouts/MainLayout';
import Dashboard from './views/Dashboard/Dashboard';
import Profile from './views/Profile/Profile';
import { RoleList } from './views/Role/RoleList';
import CreateRole from './views/Role/RoleCreate';
import RoleEdit from './views/Role/RoleEdit';
import PermissionList from './views/Permission/PermissionList';
import { UserList } from './views/Users/UserList';
import PrivateRoute from './components/PrivateRoute';
import NotFoundPage from './views/NotFound';
import ForgotToNewPassword from './views/Auth/ForgotToNewPassword';
import EmailVerifySend from './views/Auth/EmailVerifySend';
import GoogleSign from './views/Auth/GoogleSign';
import ProposalCreate from './views/Proposal/ProposalCreate';
import ProposalReport from './views/Proposal/ProposalReport';
import Company from './views/Company/Companies';
import CompanyNonconformities from './views/Company/CompanyNonconformities';
import CompanyControlReports from './views/Company/CompanyControlReports';
import Vallet from './views/Vallet/Vallet';
import Tickets from './modules/Ticket/TicketList';
import Notes from './modules/Note/Notes';
import Video from './views/Video/Video';
import Statuses from './modules/Status/Statuses';
import CompanyDetail from './views/Company/CompanyDetail';
import Settings from './views/Settings/Settings';
import EmailUpdateVerification from './views/Profile/EmailUpdateVerification';
import TicketList from './modules/Ticket/TicketListAdmin';
import tr_TR from 'antd/es/locale/tr_TR';
import UserDetail from './components/User/UserDetail';
import LinkedinSign from './views/Auth/LinkedinSign';
import ProjectFollowUp from './views/ProjectFollowUp/ProjectFollowUp';
import ProposalList from './components/Proposal/ProposalList';
import CustomerProposalList from './components/Proposal/CustomerProposalList';
import AgreementCreate from './components/Agreements/AgreementCreate';
import AllNotificationsPage from './views/AllNotifications/AllNotifications';
import ProposalPreview from './views/Proposal/ProposalPreview';
import AssetsGallery from './views/AssetsGallery.tsx/AssetsGallery';
import ReportTabs from './components/Reports/ReportTabs';
import { Opportunity } from './components/Opportunity/Opportunity';
import Payments from './views/Payments/Payments';
import ProfessionList from './modules/professions/views/List';
import MeasuringInstrumentList from './modules/measuring-instruments/views/List';
import PublicHolidayList from './modules/public-holidays/views/List';
import MeasuringInstrumentInventoryList from './modules/measuring-instrument-inventories/views/List';
import MeasuringInstrumentInventoryDetail from './modules/measuring-instrument-inventories/views/Detail';
import MeasuringInstrumentCalibrationList from './modules/measuring-instrument-calibrations/views/List';
import MeasuringInstrumentInspectorList from './modules/measuring-instrument-inspectors/views/List';
import InspectorList from './modules/inspectors/views/List';
import InspectorDetail from './modules/inspectors/views/Detail';
import InspectorProfessionList from './modules/inspector-professions/views/List';
import LeaveList from './modules/leaves/views/List';
import ExternalDocumentList from './modules/external-documents/views/List';
import ReportFormatList from './modules/report-formats/views/List';
import ReportFormatDetail from './modules/report-formats/views/Detail';
import ReportFormatMonthlyStatusPage from './modules/report-formats/views/MonthlyStatus';
import ReportNodeList from './modules/report-nodes/views/List';
import ReportNodeViewer from './modules/report-nodes/views/Viewer';
import ReportNodeViewerAll from './modules/report-nodes/views/ViewerAll';
import QuestionList from './modules/questions/views/List';
import AnswerList from './modules/answers/views/List';
import InspectionAppointmentList from './modules/inspection-appointments/views/List';
import Reminders from './views/Reminders/Reminders';
import InspectionAppointmentDetail from './modules/inspection-appointments/views/Detail';
import InspectionTaskList from './modules/inspection-tasks/views/List';
import PlanningDetail from './views/Planning/PlanningDetail';
import InspectorDashboard from './modules/inspectors/views/Dashboard';
import InspectorPlanPage from './modules/inspectors/views/PlanPage';
import InspectorDevicesPage from './modules/inspectors/views/DevicesPage';
import InspectorProfessionsPage from './modules/inspectors/views/ProfessionsPage';
import InspectorPlanDetail from './modules/inspectors/views/PlanDetail';
import ControlTaskPage from './modules/inspectors/views/ControlTaskPage';
import ExternalDocumentsPage from './modules/inspectors/views/ExternalDocumentsPage';
import InspectorNonconformityReasonsPage from './modules/inspectors/views/NonconformityReasonsPage';
import NegativeReasonSurveyPage from './views/Public/NegativeReasonSurveyPage';
import MailingAdmin from './views/Mailing/MailingAdmin';
import MailingSurveyPage from './views/Public/MailingSurveyPage';
import ReportVerificationPage from './views/Public/ReportVerificationPage';
import LoginByToken from './views/Auth/LoginByToken';

const customTheme = {
  token: {
    colorPrimary: `#${process.env.REACT_APP_PRIMARY_COLOR || '1677FF'}`, // Ana renk
    borderRadius: 8,
    fontFamily: "'Urbanist', sans-serif",
  },
  algorithm: theme.defaultAlgorithm,
  components: {
    Button: {
      fontWeight: 600,
    },
    Input: {},
  },
};

const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
      console.log('ScrollToTop triggered for', location.pathname);
    }, 0);
  }, [location.pathname]);

  return null;
};

type StoredRole = {
  name?: string;
};

const hasStoredRole = (roleName: string): boolean => {
  try {
    const roles = JSON.parse(localStorage.getItem('roles') || '[]');
    return Array.isArray(roles) && roles.some((role: StoredRole) => role?.name === roleName);
  } catch (e) {
    console.error('Roller parse hatası', e);
    return false;
  }
};

const ProposalRoute: React.FC = () => {
  if (hasStoredRole('INSPECTOR')) {
    return <NotFoundPage />;
  }

  if (hasStoredRole('CUSTOMER')) {
    return <CustomerProposalList defaultStatusIds={[]} />;
  }

  return <ProposalList defaultStatusIds={[]} />;
};

const App: React.FC = () => {
  return (
    <ConfigProvider theme={customTheme} locale={tr_TR}>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route element={<MainLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/inspector" element={<InspectorDashboard />} />
            <Route path="/inspector/plan" element={<InspectorPlanPage />} />
            <Route path="/inspector/external-documents" element={<ExternalDocumentsPage />} />
            <Route path="/inspector/nonconformity-reasons" element={<InspectorNonconformityReasonsPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/proposals" element={<ProposalRoute />} />
            <Route path="/proposals/create" element={<PrivateRoute element={<ProposalCreate />} blockedRole="INSPECTOR" />} />
            <Route path="/proposals/report" element={<ProposalReport />} />
            <Route path="/mailing/:tab?" element={<PrivateRoute element={<MailingAdmin />} requiredRole="ADMIN" />} />
            <Route path="/agreements/create" element={<AgreementCreate />} />
            <Route path="/companies" element={<PrivateRoute element={<Company />} blockedRole="INSPECTOR" />} />
            <Route path="/companies/:id/nonconformities" element={<CompanyNonconformities />} />
            <Route path="/companies/:id/control-reports" element={<CompanyControlReports />} />
            <Route path="/companies/:id" element={<PrivateRoute element={<CompanyDetail />} blockedRole="INSPECTOR" />} />
            <Route path="/users/:id" element={<PrivateRoute element={<UserDetail />} permission="users.show" />} />
            <Route path="/wallet" element={<Vallet />} />
            <Route path="/notes" element={<Video />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/all-tickets" element={<TicketList />} />
            <Route path="/statuses" element={<Statuses />} />
            <Route path="/settings" element={<PrivateRoute element={<Settings />} permission="settings.show" />} />
            <Route path="/settings/parameters/reports" element={<PrivateRoute element={<ReportFormatMonthlyStatusPage />} permission="settings.show" />} />
            <Route path="/settings/:tab" element={<PrivateRoute element={<Settings />} permission="settings.show" />} />
            <Route path="/all-notifications" element={<AllNotificationsPage />} />
            <Route path="/project-followup" element={<PrivateRoute element={<ProjectFollowUp />} blockedRole="INSPECTOR" />} />
            <Route path="/reports" element={<PrivateRoute element={<ReportTabs />} requiredRole="ADMIN" permission="report.show" />} />
            <Route path="/opportunity" element={<PrivateRoute element={<Opportunity />} blockedRole="INSPECTOR" />} />
            <Route path="/payments" element={<PrivateRoute element={<Payments />} blockedRole="INSPECTOR" />} />
            <Route path="/roles" element={<PrivateRoute element={<RoleList />} permission="roles.list" />} />
            <Route path="/permissions" element={<PrivateRoute element={<PermissionList />} requiredRole="SA" />} />
            <Route path="/users" element={<PrivateRoute element={<UserList />} permission="users.show" />} />

            <Route path='/professions' element={<ProfessionList />} />
            <Route path='/measuring-instruments' element={<MeasuringInstrumentList />} />
            <Route path='/public-holidays' element={<PrivateRoute element={<PublicHolidayList />} permission="settings.show" />} />
            <Route path='/measuring-instrument-inventories' element={<MeasuringInstrumentInventoryList />} />
            <Route path='/measuring-instrument-inventories/:id' element={<MeasuringInstrumentInventoryDetail />} />
            <Route path='/measuring-instrument-calibrations' element={<MeasuringInstrumentCalibrationList />} />
            <Route path='/measuring-instrument-inspectors' element={<MeasuringInstrumentInspectorList />} />
            <Route path='/inspectors' element={<InspectorList />} />
            <Route path='/inspectors/:id' element={<InspectorDetail />} />
            <Route path='/inspector-professions' element={<InspectorProfessionList />} />
            <Route path='/leaves' element={<LeaveList />} />
            <Route path='/external-documents' element={<ExternalDocumentList />} />
            <Route path='/report-formats' element={<ReportFormatList />} />
            <Route path='/report-formats/detail/:id' element={<ReportFormatDetail />} />
            <Route path='/report-formats/:id' element={<ReportFormatList />} />
            <Route path='/report-nodes' element={<ReportNodeList />} />
            <Route path='/report-nodes/view/:id' element={<ReportNodeViewer />} />
            <Route path='/report-nodes/view-all/:reportFormatUuid' element={<ReportNodeViewerAll />} />
            <Route path='/questions' element={<QuestionList />} />
            <Route path='/answers' element={<AnswerList />} />
            <Route path='/inspection-appointments' element={<InspectionAppointmentList />} />
            <Route path='/inspection-appointments/:id' element={<InspectionAppointmentDetail />} />
            <Route path='/inspection-tasks' element={<InspectionTaskList />} />
            <Route path='/planning/detail/:id' element={<PlanningDetail />} />
            <Route path='/inspector/devices' element={<InspectorDevicesPage />} />
            <Route path='/inspector/professions' element={<InspectorProfessionsPage />} />
            <Route path='/inspector/planning/detail/:id' element={<InspectorPlanDetail />} />
            <Route path='/control/:proposalId/taskId/:taskId/attribute/:attributeId/order/:order' element={<ControlTaskPage />} />
            <Route path='/control/:proposalId/taskId/:taskId/attribute/:attributeId/order/:order/report' element={<ControlTaskPage />} />
            <Route path='/reminders' element={<Reminders />} />

          </Route>
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/login-by-token/:tokenId" element={<LoginByToken />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-password-confirm" element={<ForgotToNewPassword />} />
            <Route path="/email-verify-check" element={<EmailVerifySend />} />
            <Route path="/oauth2/google/redirect-uri" element={<GoogleSign />} />
            <Route path="/oauth2/linkedin/redirect-uri" element={<LinkedinSign />} />
          </Route>
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/email-change-verification" element={<EmailUpdateVerification />} />
          <Route path="/public/negative-reason-survey" element={<NegativeReasonSurveyPage />} />
          <Route path="/public/mailing-survey" element={<MailingSurveyPage />} />
          <Route path="/report/verify/:id" element={<ReportVerificationPage />} />
          <Route path="/secret-gallery" element={<AssetsGallery />} />
          <Route path="/proposals/preview/:proposalId" element={<ProposalPreview />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
};

export default App;
